/**
 * Chat Input Component
 * 
 * Input area for sending messages to the AI Assistant.
 */

import { Icons } from './utils/icons.js';
import { t } from '../../i18n.js';

/**
 * Create the chat input component
 * @param {Object} options
 * @param {Function} options.onSend - Callback when message is sent
 * @param {Function} options.onOpenPrompts - Callback to open saved prompts modal
 * @param {function(): boolean} [options.onEscape] - Return true to consume Escape
 * @param {boolean} options.disabled - Whether input is disabled
 * @returns {HTMLElement}
 */
export function createChatInput({ onSend, onOpenPrompts, onEscape, disabled = false }) {
  const container = document.createElement('div');
  container.className = 'ai-chat-input-container';
  
  // Input wrapper with prompts button and textarea
  const inputWrapper = document.createElement('div');
  inputWrapper.className = 'ai-chat-input-wrapper';
  
  // Saved prompts button
  const promptsButton = document.createElement('button');
  promptsButton.className = 'ai-prompts-button';
  promptsButton.type = 'button';
  promptsButton.title = t('ai_prompts_button_title');
  promptsButton.innerHTML = Icons.bookmark();
  promptsButton.onclick = () => {
    if (onOpenPrompts) onOpenPrompts();
  };
  
  // Textarea
  const textarea = document.createElement('textarea');
  textarea.className = 'ai-chat-textarea';
  textarea.placeholder = t('ai_chat_placeholder');
  textarea.rows = 1;
  textarea.disabled = disabled;
  
  // Auto-expand textarea
  textarea.addEventListener('input', () => {
    autoExpandTextarea(textarea);
  });
  
  // Handle keyboard shortcuts
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && onEscape && onEscape()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  });
  
  // Send button
  const sendButton = document.createElement('button');
  sendButton.className = 'ai-send-button';
  sendButton.type = 'button';
  sendButton.disabled = disabled;
  sendButton.innerHTML = Icons.send();
  sendButton.onclick = handleSend;
  
  function handleSend() {
    const content = textarea.value.trim();
    if (!content || disabled || container._streaming) return;

    if (onSend) {
      const sent = onSend(content);
      if (sent !== false) {
        textarea.value = '';
        autoExpandTextarea(textarea);
      }
    }
  }
  
  inputWrapper.appendChild(promptsButton);
  inputWrapper.appendChild(textarea);
  inputWrapper.appendChild(sendButton);
  container.appendChild(inputWrapper);

  // Hint text
  const hint = document.createElement('div');
  hint.className = 'ai-input-hint';
  hint.textContent = t('ai_chat_hint');
  container.appendChild(hint);

  let isStreaming = false;
  container._streaming = false;

  container.setValue = (value) => {
    textarea.value = value;
    autoExpandTextarea(textarea);
  };

  container.getValue = () => textarea.value;

  container.setPlaceholder = (value) => {
    textarea.placeholder = value;
  };

  container.setHint = (value) => {
    hint.textContent = value;
  };

  container.setDisabled = (isDisabled) => {
    textarea.disabled = isDisabled || isStreaming;
    sendButton.disabled = isDisabled || isStreaming;
    container.classList.toggle('disabled', isDisabled);
  };

  container.focus = () => {
    textarea.focus();
  };

  container.setStreaming = (streaming) => {
    isStreaming = streaming;
    container._streaming = streaming;
    textarea.disabled = streaming || disabled;
    sendButton.disabled = streaming || disabled;
    sendButton.innerHTML = streaming ? Icons.loading() : Icons.send();
    sendButton.classList.toggle('loading', streaming);
    container.classList.toggle('streaming', streaming);
  };

  return container;
}

/**
 * Auto-expand textarea based on content
 */
function autoExpandTextarea(textarea) {
  textarea.style.height = 'auto';
  const maxHeight = 150;
  const newHeight = Math.min(textarea.scrollHeight, maxHeight);
  textarea.style.height = `${newHeight}px`;
  textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
}
